<script>
import _ from 'underscore'
import L from 'leaflet';
import { LMap, LMarker, LIcon, LPopup } from 'vue2-leaflet'
import Vue2LeafletGoogleMutant from 'vue2-leaflet-googlemutant'
import { GestureHandling } from 'leaflet-gesture-handling';
import LControlFullscreen from 'vue2-leaflet-fullscreen';

import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import 'leaflet/dist/leaflet.css'

import Translatable from '@src/mixins/translatable.js'
import Resizable from '@src/mixins/resizable.js'

import marker from '@assets/icons/marker.svg'

export default {
  name: 'PointMap',
  components: {
    LMap,
    LMarker,
    LIcon,
    LPopup,
    Vue2LeafletGoogleMutant,
    LControlFullscreen
  },
  mixins: [
    Translatable,
    Resizable
  ],
  props: {
    points: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      API_KEY: process.env.GOOGLE_MAPS_API_KEY,
      constants: {
        ZOOM_LEVEL: {
          CITY: 10,
          STREETS: 15
        }
      },
      mapOptions: {
        zoomSnap: 0.5,
        gestureHandling: true
      }
    }
  },
  computed: {
    markers() {
      if (this.points) {
        return this.points.map(point => {
          return {
            position: {
              latitude: point.lat,
              longitude: point.lon
            },
            icon: this.getMarkerIcon(),
            pointData: {
              image: `${process.env.VUE_APP_STATIC_URL}${_.sample(point.images)}`,
              content: point.content,
              address: point.address1,
              id: point._id,
              slug: point.slug
            }
          }
        }).filter(marker => marker.position.latitude && marker.position.longitude)
      }
      return []
    }
  },
  methods: {
    calculateCenterCoords() {
      if (this.markers.length) {
        const avgLat = this.markers.reduce((sum, marker) => {
          return (sum + marker.position.latitude)
        }, 0) / this.markers.length;

        const avgLong= this.markers.reduce((sum, marker) => {
          return (sum + marker.position.longitude)
        }, 0) / this.markers.length;

        return {
          lat: avgLat,
          lng: avgLong
        }
      }

      return [50.083, 14.417]
    },
    centerMap(marker) {
      this.$refs.map.setCenter(this.getPosition(marker))
    },
    getMarkerIcon() {
      return `${marker}`
    },
    getPosition(marker) {
      return [marker.position.latitude, marker.position.longitude]
    },
    getBackground(img) {
      return {
        backgroundImage: `url(${img})`
      }
    },
    openPopup: function (event) {
      if (this.desktopScreen) event.target.openPopup()
    },
    closePopup: function (event) {
      if (this.desktopScreen) event.target.closePopup()
    },
    navigate(marker) {
      if (!this.desktopScreen) {
        this.centerMap(marker)
      } else {
        this.$router.push({
          name: 'point',
          params: {
            slug: marker.pointData.slug,
          }
        })
      }
		},
  },
  mounted() {
    const map = this.$refs.map.mapObject;
    map.addLayer(L.tileLayer('https://api.mapbox.com/styles/v1/anton2332/cl78v0qvv001g15nq9t01x6j8/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoiYW50b24yMzMyIiwiYSI6ImNsNzh0cGhidDBkMmUzcnBnd2NkbHNncTYifQ.VW69-qMjWmdNfHwNI4VXcA', {
      attribution: '© <a href="https://www.mapbox.com/">Mapbox</a>',
    }));
    this.$refs.map.addInitHook('addHandler', 'gestureHandling', GestureHandling);
  },
}
</script>

<template>
  <div class="map-wrapper">
    <LMap
      ref="map"
      :zoom="constants.ZOOM_LEVEL.STREETS"
      :min-zoom="constants.ZOOM_LEVEL.CITY"
      :center="calculateCenterCoords()"
      :options="mapOptions"
    >
      <Vue2LeafletGoogleMutant :apikey="API_KEY"/>
      <LMarker
        v-for="(marker, index) in markers"
        :key="index"
        :lat-lng="getPosition(marker)"
        @click="navigate(marker)"
        @mouseenter="openPopup"
        @mouseleave="closePopup"
      >
        <LIcon :icon-url="getMarkerIcon()"/>
        <LPopup>
          <router-link
            :to="{
              name: 'point',
              params: {
                slug: marker.pointData.slug,
              },
            }"
            class="link"
          >
            <div class="popup-container">
              <div class="point-image" :style="getBackground(marker.pointData.image)">
              </div>
              <div class="text-content">
                <h5 class="point-title-popup">{{ getTranslatedContent('title', marker.pointData.content) }}</h5>
                <p class="address-line">{{ marker.pointData.address }}</p>
              </div>
            </div>
          </router-link>
        </LPopup>
      </LMarker>
      <LControlFullscreen v-if="!desktopScreen" position="bottomleft"/>
    </LMap>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.link {
  text-decoration: none;
}

.map-wrapper {
  height: 638px;
}

.google-map {
  width: 100%;
  height: 100%;
}

.google-map-expanded {
  position: absolute;
  width: 100%;
  height: 643px;
  left: 0;
}

.popup-container {
  width: 100%;
  min-height: 203px;
  padding-bottom: 13px;

  & .point-image {
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;

    width: 231px;
    height: 136px;

    // border: 1px solid #FFFFFF;
    box-sizing: border-box;
    border-radius: 5px 5px 0 0;
  }

  & .text-content {
    padding-left: 13px;
    padding-right: 9px;
    padding-top: 11px;

    & h5 {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
      font-size: 14px;
      line-height: 18px;
      text-transform: uppercase;
      color: #252527;
    }

    & .address-line {
      font-family: "Open Sans", sans-serif;
      font-style: normal;
      font-weight: 600;
      font-size: 12px;
      line-height: 16px;
      color: rgba(37, 44, 62, 0.6);
      margin: 7px 0 0;
    }
  }
}

// leaflet css overrides

::v-deep .leaflet-popup-content {
  margin: 0;
  width: 231px !important;
}

::v-deep .leaflet-popup-close-button {
  display: none;
}

::v-deep .leaflet-popup-content-wrapper {
  border-radius: 5px;
  padding: 0;
}

::v-deep .leaflet-popup {
  margin-bottom: 70px !important;
}

::v-deep .leaflet-control-attribution.leaflet-control {
  display: none !important;
}

@media only screen and (max-width: $screen-width-xs + 1) {
  .map-wrapper {
    height: 568px;
  }
}
</style>
